import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { IconAlertTriangle, IconLock } from '@tabler/icons-react';
import { Navigate, useLocation } from 'react-router';

import { useSession } from '@/auth/session';

import { decideAccess, needsSession } from './access';
import { BackOfficeShell } from './BackOfficeShell';
import { paths } from './paths';
import { returnPathFrom } from './return-path';
import type { RouteDefinition } from './route-table';

/**
 * The door every route in this app is rendered behind.
 *
 * `App.tsx` wraps every entry in the route table with this, so a screen cannot
 * be reached without passing it and a new route cannot forget to be guarded —
 * only to be declared, which the required `access` field and the start-up audit
 * both refuse.
 *
 * What it decides is in `access.ts` and is a pure function. What is left here is
 * the four things a decision turns into on screen: a redirect to sign in that
 * remembers where you were going, a redirect away from a screen you are past, a
 * refusal you can read, and the loud complaint about a route that never said who
 * it was for.
 *
 * It is not authorisation, and the distinction is worth keeping sharp because
 * everything here is drawn from a session restored out of `sessionStorage` —
 * shape-checked, not verified, and editable by whoever is at the keyboard. A
 * user who edits their role into `OWNER` gets the owner's navigation drawn for
 * them and every request they then make refused by the server, which is the only
 * thing that was ever deciding. This makes the app coherent; the API makes it
 * safe.
 */
export function RouteGuard({ route }: { route: RouteDefinition }) {
  const { session } = useSession();
  const location = useLocation();

  const viewer =
    session === null
      ? null
      : {
          role: session.user.role,
          mustChangeCredential: session.user.mustChangeCredential,
        };

  const outcome = decideAccess(route.access, viewer);

  switch (outcome) {
    case 'render':
      return needsSession(route.access) ? (
        <BackOfficeShell>{route.element}</BackOfficeShell>
      ) : (
        route.element
      );

    case 'sign-in-first':
      // Where they were going, carried in the router's location state rather
      // than in the URL — a destination in a query string is a link anybody can
      // write, and a link anybody can write that decides where the browser goes
      // next is an open redirect. `return-path.ts` re-checks it on the way out
      // regardless, because history state survives a reload and comes back
      // untyped.
      //
      // `replace`, so the back button does not offer to return to a screen the
      // viewer was just refused.
      return (
        <Navigate
          to={paths.signIn}
          replace
          state={{ from: `${location.pathname}${location.search}` }}
        />
      );

    case 'already-signed-in':
      // Signed in, on a signed-out screen — almost always somebody who has this
      // moment finished signing in. This is the single place the post-sign-in
      // destination is decided, so there is no second navigation racing it.
      return <Navigate to={returnPathFrom(location.state) ?? paths.dashboard} replace />;

    case 'must-change-credential':
      // The API refuses every route but its public ones and this one until the
      // credential somebody else chose has been replaced. Sending them to the
      // gate is the difference between a screen that explains itself and a
      // screen where every control answers 403.
      //
      // The equality check breaks a loop rather than being dead code: if the
      // gate route is ever declared without admitting a pending credential, the
      // redirect below would target the screen we are already on, forever. The
      // start-up audit calls that a defect; this is what happens if one reaches
      // a browser anyway, and a legible complaint beats a hung tab.
      return location.pathname === paths.changeCredential ? (
        <MisdeclaredCredentialGate />
      ) : (
        <Navigate to={paths.changeCredential} replace />
      );

    case 'refused':
      // A refusal and not a redirect. Signing in again cannot give somebody a
      // role they do not hold, so bouncing them to a sign-in screen they are
      // already past would be a loop with a misleading explanation attached.
      //
      // Inside the shell, because they are signed in and this is one screen
      // they cannot open rather than the end of the road. A bare refusal would
      // leave them with the browser's back button as their only way on.
      return (
        <BackOfficeShell>
          <AccessRefused />
        </BackOfficeShell>
      );

    case 'undeclared':
      return <RouteUndeclared path={route.path} />;
  }
}

/**
 * Signed in, and this screen is not for them.
 *
 * It names neither the role they hold nor the roles that would have worked. The
 * API's refusals are deliberately silent about both — a refusal that enumerated
 * what would have worked is a map of the permission model — and there is no
 * reason for the client to be more forthcoming than the server it is describing.
 */
function AccessRefused() {
  return (
    <main className="flex flex-1 justify-center p-6">
      <Alert className="my-auto max-w-md">
        <IconLock />
        <AlertTitle>You do not have access to this page</AlertTitle>
        <AlertDescription>
          Your role does not include this part of the back office. Ask an owner if you need it.
        </AlertDescription>
      </Alert>
    </main>
  );
}

/**
 * A route that never said who may see it, refused and named.
 *
 * This is the shape that only reaches a browser in a production build — in
 * development and under test the start-up audit has already thrown, with a
 * message naming every offender. What matters is that it is not silent. Deny by
 * default rendering nothing at all is the failure mode worth avoiding: a blank
 * screen reads as a bug in the screen, gets "fixed" by loosening the guard, and
 * nobody learns a declaration was missing.
 */
function RouteUndeclared({ path }: { path: string }) {
  return (
    <main className="flex min-h-screen justify-center p-6">
      <div className="my-auto w-full max-w-lg">
        <Alert variant="destructive">
          <IconAlertTriangle />
          <AlertTitle>This page has not declared who may see it</AlertTitle>
          <AlertDescription>
            <p>
              <code className="numeric">{path}</code> is refused because its entry in the route
              table carries no access requirement — not because of anything you did. A route that
              has not said who may see it is forbidden, not public.
            </p>
            <p>
              Report it. Whoever added the route needs to declare it with publicRoute(),
              signedOutOnly(), requireSignedIn() or requireRole(…).
            </p>
          </AlertDescription>
        </Alert>
      </div>
    </main>
  );
}

/**
 * The must-change-credential gate, declared so that it refuses the very people
 * it exists to admit.
 *
 * Rendered instead of redirecting to itself. Same reasoning as above: the loud
 * version of this mistake is a sentence naming it, and the quiet version is a
 * tab that hangs.
 */
function MisdeclaredCredentialGate() {
  return (
    <main className="flex min-h-screen justify-center p-6">
      <div className="my-auto w-full max-w-lg">
        <Alert variant="destructive">
          <IconAlertTriangle />
          <AlertTitle>This page cannot let you change your credential</AlertTitle>
          <AlertDescription>
            <p>
              <code className="numeric">{paths.changeCredential}</code> is where you are sent to
              replace the credential you were given, but it is declared as though you had already
              replaced it, so it refuses you.
            </p>
            <p>Report it. Nothing you can do from here will fix it.</p>
          </AlertDescription>
        </Alert>
      </div>
    </main>
  );
}
